import { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, BarChart3, ChevronLeft, ChevronRight } from "lucide-react";
import { format, addMonths, subMonths, addWeeks, subWeeks, startOfMonth, endOfMonth, startOfYear, endOfYear } from "date-fns";
import { fr } from "date-fns/locale";
import { useGanttData } from "@/hooks/useGanttData";
import { GanttTimeline } from "@/components/GanttTimeline";
import { GanttRow } from "@/components/GanttRow";
interface AppelOffre {
  id: string;
  titre: string;
  reference: string;
  date_limite: string;
  date_publication?: string;
  date_debut_redaction_ao?: string;
  date_qr_soumissionnaires?: string;
  date_reponses_qr?: string;
  date_analyse_offres?: string;
  date_debut_soutenance?: string;
  date_fin_soutenance?: string;
  date_adjudication?: string;
  date_contractualisation?: string;
  interlocuteur_da?: string;
}
interface GanttViewProps {
  appelOffres: AppelOffre[];
  selectedYear: number;
  onAOClick: (aoId: string) => void;
}
interface GanttTask {
  id: string;
  title: string;
  reference: string;
  startDate: Date;
  endDate: Date;
  type: 'redaction' | 'publication' | 'deadline' | 'qr' | 'analyse' | 'soutenance' | 'adjudication' | 'contractualisation';
  status: 'expired' | 'urgent' | 'upcoming';
  originalAO: AppelOffre;
}
type ViewMode = 'monthly' | 'weekly' | 'daily';
const GanttView = ({
  appelOffres,
  selectedYear,
  onAOClick
}: GanttViewProps) => {
  const [viewMode, setViewMode] = useState<ViewMode>('monthly');
  const [viewStartDate, setViewStartDate] = useState(() => startOfYear(new Date(selectedYear, 0)));
  const [viewEndDate, setViewEndDate] = useState(() => endOfYear(new Date(selectedYear, 0)));

  // Refs for scroll synchronization
  const timelineRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Calculate dynamic column width based on view mode
  const columnWidth = viewMode === 'daily' ? 32 : viewMode === 'weekly' ? 80 : 120;

  // Reset view dates when selectedYear prop changes
  useEffect(() => {
    setViewStartDate(startOfYear(new Date(selectedYear, 0)));
    setViewEndDate(endOfYear(new Date(selectedYear, 0)));
    setViewMode('monthly'); // Reset to monthly view when year changes
  }, [selectedYear]);

  // Update view dates when view mode changes
  useEffect(() => {
    const currentDate = new Date(selectedYear, new Date().getMonth());
    switch (viewMode) {
      case 'daily':
        // Show current month in daily view
        setViewStartDate(startOfMonth(currentDate));
        setViewEndDate(endOfMonth(currentDate));
        break;
      case 'weekly':
        // Show current month + buffer in weekly view  
        setViewStartDate(startOfMonth(subMonths(currentDate, 1)));
        setViewEndDate(endOfMonth(addMonths(currentDate, 1)));
        break;
      case 'monthly':
      default:
        // Show full year in monthly view
        setViewStartDate(startOfYear(new Date(selectedYear, 0)));
        setViewEndDate(endOfYear(new Date(selectedYear, 0)));
        break;
    }
  }, [viewMode]);

  // Use custom hook for data management
  const {
    ganttTasks,
    timelineColumns,
    stats,
    tasksByAO
  } = useGanttData(appelOffres, selectedYear, viewStartDate, viewEndDate, viewMode);

  // Calculate timeline width
  const timelineWidth = timelineColumns.length * columnWidth;
  const navigateTime = (direction: 'prev' | 'next') => {
    switch (viewMode) {
      case 'daily':
        if (direction === 'prev') {
          const newStart = subMonths(viewStartDate, 1);
          setViewStartDate(startOfMonth(newStart));
          setViewEndDate(endOfMonth(newStart));
        } else {
          const newStart = addMonths(viewStartDate, 1);
          setViewStartDate(startOfMonth(newStart));
          setViewEndDate(endOfMonth(newStart));
        }
        break;
      case 'weekly':
        if (direction === 'prev') {
          setViewStartDate(subWeeks(viewStartDate, 4));
          setViewEndDate(subWeeks(viewEndDate, 4));
        } else {
          setViewStartDate(addWeeks(viewStartDate, 4));
          setViewEndDate(addWeeks(viewEndDate, 4));
        }
        break;
      case 'monthly':
      default:
        const currentViewYear = viewStartDate.getFullYear();
        if (direction === 'prev') {
          setViewStartDate(startOfYear(new Date(currentViewYear - 1, 0)));
          setViewEndDate(endOfYear(new Date(currentViewYear - 1, 0)));
        } else {
          setViewStartDate(startOfYear(new Date(currentViewYear + 1, 0)));
          setViewEndDate(endOfYear(new Date(currentViewYear + 1, 0)));
        }
        break;
    }
  };

  // Get current period label
  const getCurrentPeriodLabel = () => {
    switch (viewMode) {
      case 'daily':
        return format(viewStartDate, 'MMMM yyyy', {
          locale: fr
        });
      case 'weekly':
        return `${format(viewStartDate, 'MMM', {
          locale: fr
        })} - ${format(viewEndDate, 'MMM yyyy', {
          locale: fr
        })}`;
      case 'monthly':
      default:
        return format(viewStartDate, 'yyyy');
    }
  };

  // Scroll synchronization
  const handleTimelineScroll = useCallback((scrollLeft: number) => {
    rowRefs.current.forEach(ref => {
      if (ref && ref.scrollLeft !== scrollLeft) {
        ref.scrollLeft = scrollLeft;
      }
    });
  }, []);
  const handleRowScroll = useCallback((scrollLeft: number) => {
    if (timelineRef.current && timelineRef.current.scrollLeft !== scrollLeft) {
      timelineRef.current.scrollLeft = scrollLeft;
    }
    rowRefs.current.forEach(ref => {
      if (ref && ref.scrollLeft !== scrollLeft) {
        ref.scrollLeft = scrollLeft;
      }
    });
  }, []);

  // Update row refs array size
  const aoIds = Array.from(tasksByAO.keys());
  if (rowRefs.current.length !== aoIds.length) {
    rowRefs.current = Array(aoIds.length).fill(null);
  }
  return <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 gap-4">
        <Card className="card-shadow">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Analyse</p>
                <p className="text-2xl font-bold text-foreground">{stats.total}</p>
              </div>
              <BarChart3 className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gantt Chart */}
      <Card className="card-shadow">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center">
                <Calendar className="h-5 w-5 mr-2 text-primary" />
                Planning Gantt {selectedYear}
              </CardTitle>
              <CardDescription>
                Vue chronologique avec granularité {viewMode === 'daily' ? 'quotidienne' : viewMode === 'weekly' ? 'hebdomadaire' : 'mensuelle'}
              </CardDescription>
            </div>
            <div className="flex items-center gap-4">
              {/* View Mode Selector */}
              <Select value={viewMode} onValueChange={(value: ViewMode) => setViewMode(value)}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Mensuel</SelectItem>
                  <SelectItem value="weekly">Hebdomadaire</SelectItem>
                  <SelectItem value="daily">Quotidien</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Navigation Controls */}
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => navigateTime('prev')}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium min-w-40 text-center">
                  {getCurrentPeriodLabel()}
                </span>
                <Button variant="outline" size="sm" onClick={() => navigateTime('next')}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {ganttTasks.length > 0 ? <div className="space-y-4">
              {/* Timeline Header */}
              <GanttTimeline ref={timelineRef} columns={timelineColumns} columnWidth={columnWidth} viewMode={viewMode} onScroll={handleTimelineScroll} />

              {/* Gantt Rows */}
              <ScrollArea className="h-96">
                <div className="space-y-3">
                  {aoIds.map((aoId, index) => {
                const tasks = tasksByAO.get(aoId)!;
                const ao = tasks[0].originalAO;
                return <GanttRow key={aoId} ref={el => rowRefs.current[index] = el} aoId={aoId} ao={ao} tasks={tasks} viewStartDate={viewStartDate} viewEndDate={viewEndDate} viewMode={viewMode} columnWidth={columnWidth} timelineWidth={timelineWidth} timelineColumns={timelineColumns} onAOClick={onAOClick} onScroll={handleRowScroll} />;
              })}
                </div>
              </ScrollArea>
            </div> : <div className="text-center py-12">
              <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                Aucun appel d'offres trouvé pour l'année {selectedYear}
              </p>
            </div>}
        </CardContent>
      </Card>

      {/* Legend */}
      <Card className="card-shadow">
        <CardHeader>
          <CardTitle className="text-lg">Phase planning</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="flex items-center gap-3">
              <div className="w-6 h-4 bg-orange-500 rounded-sm"></div>
              <span className="text-sm text-muted-foreground">Rédaction / Publication / Limite</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-6 h-4 bg-purple-500 rounded-sm"></div>
              <span className="text-sm text-muted-foreground">Q&R</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-6 h-4 bg-cyan-500 rounded-sm"></div>
              <span className="text-sm text-muted-foreground">Analyse</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-6 h-4 bg-pink-500 rounded-sm"></div>
              <span className="text-sm text-muted-foreground">Soutenance</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-6 h-4 bg-emerald-500 rounded-sm"></div>
              <span className="text-sm text-muted-foreground">Adjudication</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-6 h-4 bg-amber-600 rounded-sm"></div>
              <span className="text-sm text-muted-foreground">Contractualisation</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>;
};
export default GanttView;